import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface AdminProfileDetails {
  user_role: string;
  user_code: string;
  first_name: string;
  last_name: string;
  country_code: string;
  phone_number: string;
  house_no: string;
  floor_no: string;
  market_block_no: string;
  street_name: string;
  city_town: string;
  district: string;
  region: string;
  province: string;
  country: string;
  postal_code: string;
  email_address: string;
  wallet_access: string[];
  phone_number_verify: string;
}

interface AdminProfileLoader {
  phone_number: boolean;
}

interface AdminProfileState {
  profileDetails: AdminProfileDetails;
  loading: boolean;
  errors: Partial<Record<keyof AdminProfileDetails, string>>;
  loader: AdminProfileLoader;
  disableAll: boolean;
  editData: AdminProfileDetails;
}

const initialDetails: AdminProfileDetails = {
  user_role: '',
  user_code: '',
  first_name: '',
  last_name: '',
  country_code: '',
  phone_number: '',
  house_no: '',
  floor_no: '',
  market_block_no: '',
  street_name: '',
  city_town: '',
  district: '',
  region: '',
  province: '',
  country: '',
  postal_code: '',
  email_address: '',
  wallet_access: [],
  phone_number_verify: '',
};

const initialState: AdminProfileState = {
  profileDetails: { ...initialDetails },
  loading: false,
  errors: {},
  loader: { phone_number: false },
  disableAll: false,
  editData: { ...initialDetails },
};

const adminProfileSlice = createSlice({
  name: 'adminProfile',
  initialState,
  reducers: {
    resetAdminProfile: () => ({ ...initialState }),
    setUpdateProfileStates: (
      state,
      action: PayloadAction<{ key: string; value: string | string[] }>
    ) => {
      const { key, value } = action.payload;
      if (key in state.profileDetails) {
        (state.profileDetails as any)[key] = value;
        state.errors[key as keyof AdminProfileDetails] = '';
      }
    },
    setProfileError: (
      state,
      action: PayloadAction<Partial<AdminProfileDetails>>
    ) => {
      const errorPayload: Partial<Record<keyof AdminProfileDetails, string>> = {};
      Object.entries(action.payload).forEach(([key, value]) => {
        if (key === 'wallet_access') {
          errorPayload[key as keyof AdminProfileDetails] = '';
        } else if (typeof value === 'string') {
          errorPayload[key as keyof AdminProfileDetails] = value;
        }
      });
      state.errors = { ...state.errors, ...errorPayload };
    },
    setProfileLoader: (
      state,
      action: PayloadAction<{ key: string; value: boolean }>
    ) => {
      const { key, value } = action.payload;
      if (key in state.loader) {
        state.loader[key as keyof AdminProfileLoader] = value;
        state.disableAll = value;
      }
    },
    setEditData: (
      state,
      action: PayloadAction<AdminProfileDetails>
    ) => {
      state.editData = { ...action.payload };
    },
  },
});

export const {
  resetAdminProfile,
  setUpdateProfileStates,
  setProfileError,
  setProfileLoader,
  setEditData,
} = adminProfileSlice.actions;

export default adminProfileSlice.reducer; 